import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Activity,
  X,
  Plus,
  Loader2,
  CalendarCheck,
  Video,
  MapPin,
  Stethoscope,
  FileText,
  ExternalLink,
  Check,
  Phone,
  VideoOff
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import VideoCallModal from './VideoCallModal';
import { io } from 'socket.io-client';

const socket = io(`${window.location.protocol}//${window.location.hostname}`);

const TelemedicineAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCall, setShowCall] = useState(false);
  const [loading, setLoading] = useState(true);

  const userName = localStorage.getItem('userName') || 'Doctor';
  const role = localStorage.getItem('userRole');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAppointments();
  }, [selectedDate]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${window.location.protocol}//${window.location.hostname}/api/appointments`, { headers });
      // Only keep telemedicine appointments
      const virtualApps = response.data.filter(a => a.isVirtual === true);
      setAppointments(virtualApps);
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Failed to load virtual clinic');
    } finally {
      setLoading(false);
    }
  };

  const getWeekDays = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay() + (start.getDay() === 0 ? -6 : 1)); // Start from Monday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  const weekDays = getWeekDays(selectedDate);
  const timeSlots = Array.from({ length: 15 }, (_, i) => `${(i + 8).toString().padStart(2, '0')}:00`); 

  const handlePrevWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 7);
    setSelectedDate(d);
  };

  const handleNextWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 7);
    setSelectedDate(d);
  };

  const openAppointment = (appt) => {
    setSelectedAppointment(appt);
    setShowModal(true);
  };

  const startCall = (appt) => {
      // Signal to the patient using their ID (mapped to a socket room)
      const pId = appt.patientId || appt.PatientId || appt.Patient?.id;
      const dId = appt.doctorId || appt.DoctorId || appt.Doctor?.id;

      if (role !== 'patient' && pId) {
          socket.emit('call-user', {
              toUserId: pId,
              fromUserName: `Dr. ${userName}`,
              appointmentId: appt.id,
              patientId: pId,
              doctorId: dId
          });
      }

      setSelectedAppointment(appt);
      setShowCall(true);
      setShowModal(false);
  };

  if (loading && appointments.length === 0) return (
    <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
    </div>
  );

  return (
    <div className="p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
      <div className="max-w-full mx-auto space-y-4">
        
        {/* Cinematic Telemedicine Header */}
        <div className="bg-white/80 backdrop-blur-md px-8 py-5 rounded-[24px] border border-slate-200 shadow-xl shadow-slate-200/30 flex justify-between items-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-purple-500 via-indigo-500 to-emerald-500" />
            <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-200">
                    <Video className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h1 className="text-xl font-black text-slate-800 tracking-tight">Virtual Clinic</h1>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Telemedicine Appointment Matrix</p>
                </div>
            </div>

            <div className="flex items-center gap-3">
                <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl p-1 shadow-inner">
                    <button onClick={handlePrevWeek} className="p-1.5 hover:bg-white hover:shadow-sm rounded-lg transition-all text-slate-500">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button 
                        onClick={() => setSelectedDate(new Date())}
                        className="px-5 py-1.5 text-[10px] font-black text-slate-700 uppercase tracking-widest hover:text-indigo-600 transition-colors"
                    >
                        Today
                    </button>
                    <button onClick={handleNextWeek} className="p-1.5 hover:bg-white hover:shadow-sm rounded-lg transition-all text-slate-500">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                
                <h2 className="text-lg font-black text-slate-800 tracking-tight min-w-[150px] text-center">
                    {weekDays[0].toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </h2>

                <div className="h-8 w-[1px] bg-slate-100 mx-2" />

                <Link 
                    to="/dashboard/patient-admin/appointments/book-doctor-appointment" 
                    className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-black text-white rounded-xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-purple-100 transition-all active:scale-95 group"
                >
                    <Plus className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform" />
                    Book Virtual
                </Link>
            </div>
        </div>

        {/* Compact Calendar Grid */}
        <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[calc(100vh-160px)]">
          <div className="flex-grow overflow-auto custom-scrollbar">
            <div className="min-w-[1100px] h-full flex flex-col">
              
              {/* Day Titles */}
              <div className="grid grid-cols-[80px_repeat(7,1fr)] border-b border-slate-100 sticky top-0 z-10 bg-white">
                <div className="p-4 text-[10px] font-black text-slate-400 uppercase tracking-widest border-r border-slate-50 flex items-center justify-center">GMT Matrix</div>
                {weekDays.map((day, idx) => {
                  const isToday = day.toDateString() === new Date().toDateString();
                  return (
                    <div key={idx} className={`p-4 text-center border-r border-slate-50 last:border-0 relative ${isToday ? 'bg-purple-50/20' : ''}`}>
                      <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">
                        {day.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div className={`text-xl font-black ${isToday ? 'text-purple-600' : 'text-slate-800'}`}>
                        {day.getDate().toString().padStart(2, '0')}
                      </div>
                      {isToday && <div className="absolute top-0 left-0 w-full h-[2px] bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.5)]" />}
                    </div>
                  );
                })}
              </div>

              {/* Matrix Cells */}
              <div className="flex-grow relative bg-slate-50/10">
                {timeSlots.map((time, tIdx) => (
                  <div key={tIdx} className="grid grid-cols-[80px_repeat(7,1fr)] group min-h-[50px] border-b border-slate-50/50">
                    <div className="p-2 text-[10px] font-black text-slate-300 border-r border-slate-50 flex justify-center items-center font-mono group-hover:text-purple-400 transition-colors">
                      {time}
                    </div>
                    {weekDays.map((day, dIdx) => {
                      const cellDateStr = day.toISOString().split('T')[0];
                      const cellApps = appointments.filter(a => {
                         const start = new Date(a.startTime);
                         return start.toISOString().split('T')[0] === cellDateStr && 
                                start.getHours().toString().padStart(2, '0') === time.split(':')[0];
                      });

                      return (
                        <div key={dIdx} className="border-r border-slate-50/50 last:border-0 relative hover:bg-white/80 transition-all p-1.5 min-h-[60px]">
                          {cellApps.map((appt, aIdx) => (
                            <div 
                              key={aIdx}
                              onClick={() => openAppointment(appt)}
                              className="rounded-xl p-3 text-[11px] mb-1.5 transition-all cursor-pointer relative overflow-hidden group/appt border-l-4 border-purple-400 bg-purple-600 text-white shadow-purple-100 hover:scale-[1.03] hover:shadow-xl shadow-sm"
                            >
                              <div className="font-black truncate text-[12px] pr-4">{appt.patientName || `${appt.Patient?.firstName} ${appt.Patient?.lastName}`}</div>
                              <div className="flex items-center gap-1.5 mt-1 text-[9px] font-black uppercase tracking-tighter opacity-80 whitespace-nowrap">
                                <Stethoscope className="w-2.5 h-2.5 shrink-0" />
                                <span className="truncate">{appt.doctorName || appt.Doctor?.username}</span>
                              </div>
                              <div className="flex items-center gap-1.5 mt-1.5">
                                 <span className="px-1.5 py-0.5 bg-white/20 rounded-md text-[8px] font-black uppercase tracking-widest shrink-0">
                                    {appt.AppointmentType?.typeName || 'General'}
                                 </span>
                                 <Video className="w-3 h-3 text-white/90 animate-pulse" />
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {appointments.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center p-20 pointer-events-none">
                  <div className="text-center space-y-4">
                      <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto opacity-50">
                          <VideoOff className="w-10 h-10 text-slate-200" />
                      </div>
                      <div className="text-slate-300 font-black uppercase tracking-widest text-[11px]">No virtual appointments found in this range</div>
                  </div>
              </div>
          )}
        </div>
      </div>

      {/* Appointment Detail Modal (Enhanced for Telemedicine) */}
      {showModal && selectedAppointment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[40px] w-full max-w-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 relative border border-slate-100">
            
            {/* Modal Hero Header */}
            <div className="p-10 relative overflow-hidden bg-purple-600">
                <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
                <button 
                    onClick={() => setShowModal(false)}
                    className="absolute top-8 right-8 p-3 bg-white/15 hover:bg-white/30 rounded-2xl transition-all text-white backdrop-blur-sm"
                >
                    <X className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-6 relative z-10">
                    <div className="w-20 h-20 bg-white shadow-2xl shadow-black/20 rounded-[28px] flex items-center justify-center text-3xl font-black text-purple-600 border-4 border-white/20 shrink-0">
                        {String(selectedAppointment.patientName || selectedAppointment.Patient?.firstName)[0]}
                    </div>
                    <div>
                        <h3 className="text-3xl font-black text-white tracking-tight leading-tight">
                            {selectedAppointment.patientName || `${selectedAppointment.Patient?.firstName} ${selectedAppointment.Patient?.lastName}`}
                        </h3>
                        <div className="flex items-center gap-4 mt-2">
                             <div className="flex items-center gap-1.5 text-purple-100 font-bold text-sm">
                                <CalendarIcon className="w-4 h-4 text-white/70" />
                                {new Date(selectedAppointment.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                             </div>
                             <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                             <div className="flex items-center gap-1.5 text-purple-100 font-bold text-sm uppercase tracking-widest text-[11px]">
                                <Clock className="w-4 h-4 text-white/70" />
                                {new Date(selectedAppointment.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                             </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-10 space-y-8">
                {/* Meta Grid */}
                <div className="grid grid-cols-2 gap-8">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <Stethoscope className="w-3 h-3" /> Assigned Practitioner
                        </label>
                        <div className="text-slate-800 font-black text-lg leading-tight uppercase tracking-tight">
                            {selectedAppointment.doctorName || selectedAppointment.Doctor?.username}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <Activity className="w-3 h-3" /> Session Category
                        </label>
                        <div className="inline-flex px-3 py-1 bg-purple-50 text-purple-600 rounded-lg font-black text-[12px] uppercase tracking-wide border border-purple-100">
                             {selectedAppointment.AppointmentType?.typeName || 'Online Setup'}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-8 border-t border-slate-50 pt-8">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <Video className="w-3 h-3" /> Communication Protocol
                        </label>
                        <div className="flex items-center gap-2 font-bold text-slate-700 text-sm">
                            <span className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-lg text-[10px] font-black uppercase flex items-center gap-2 tracking-widest">
                                <Check className="w-3 h-3" /> Secure E2E Video
                            </span>
                        </div>
                    </div>
                    <div className="space-y-2 text-right">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Digital Branch</label>
                        <div className="text-slate-500 font-black text-xs uppercase tracking-widest">
                            {selectedAppointment.branch || 'Virtual Health Hub'}
                        </div>
                    </div>
                </div>

                {selectedAppointment.notes && (
                    <div className="bg-slate-50 p-6 rounded-[24px] border border-slate-200/50 italic text-slate-500 text-sm font-medium leading-relaxed relative">
                        <FileText className="absolute -top-3 -left-3 w-8 h-8 text-purple-100" />
                        "{selectedAppointment.notes}"
                    </div>
                )}

                <div className="flex gap-4 pt-4">
                    <button 
                        onClick={() => startCall(selectedAppointment)}
                        className="flex-[2] py-4 bg-purple-600 hover:bg-black text-white font-black text-[11px] uppercase tracking-widest rounded-2xl shadow-xl shadow-purple-200 transition-all flex items-center justify-center gap-3 active:scale-95 group"
                    >
                        <Video className="w-4 h-4 group-hover:animate-bounce" /> Start Video Consultation
                    </button>
                    <button className="flex-1 py-4 bg-slate-900 hover:bg-black text-white font-black text-[11px] uppercase tracking-widest rounded-2xl shadow-xl shadow-slate-200 transition-all flex items-center justify-center gap-2 active:scale-95">
                        <ExternalLink className="w-4 h-4" /> Reports
                    </button>
                </div>
            </div>
          </div>
        </div>
      )}
      {/* Video Call Modal (Jitsi Integration) */}
      <VideoCallModal 
        isOpen={showCall} 
        onClose={() => setShowCall(false)} 
        appointment={selectedAppointment} 
      />
    </div>
  );
};

export default TelemedicineAppointments;
